import { useState, useEffect } from 'react';
import { getHeaderOffset } from './styles';
import { SITE_HEADER_ID } from './headerConstants';
import { usesCompactSearchBar } from '../../siteConfigs/serviceCategories';
import { useResultsSearchContext } from './MobileResultsSearchContext';

export { SITE_HEADER_ID };

/** Live pixels from viewport top to below header (for map layers). */
export function measureSiteMapTop() {
  const layout = readHeaderLayout();
  if (!layout) {
    return 200;
  }
  syncMapTopCssVar(layout.mapTop);
  return layout.mapTop;
}

/** Box-shadow / pill overflow below the header layout box (expanded search). */
const HEADER_LAYOUT_BUFFER_EXPANDED_PX = 18;
const HEADER_LAYOUT_BUFFER_LANDING_PX = 10;
import { SITE_HEADER_OFFSET_VAR } from '../../utils/siteLayoutOffsets';
const SITE_MAP_TOP_VAR = '--site-map-top';

export function syncSiteHeaderOffsetCssVar(heightPx) {
  if (typeof document === 'undefined' || heightPx == null) {
    return;
  }
  document.documentElement.style.setProperty(
    SITE_HEADER_OFFSET_VAR,
    `${Math.ceil(heightPx)}px`,
  );
}

function readHeaderLayout() {
  const header = document.getElementById(SITE_HEADER_ID);
  if (!header) {
    return null;
  }
  const compact = header.dataset.compactSearch === 'true';
  const expanded = header.dataset.searchExpanded === 'true';
  const buffer =
    compact && !expanded
      ? 0
      : expanded
        ? HEADER_LAYOUT_BUFFER_EXPANDED_PX
        : HEADER_LAYOUT_BUFFER_LANDING_PX;
  const mapGap = compact && !expanded ? 8 : 12;
  const rect = header.getBoundingClientRect();

  const height = Math.ceil(rect.height);
  const offset = height + buffer;
  const mapTop = Math.ceil(rect.bottom) + mapGap;

  return { height, offset, mapTop };
}

function readHeaderOffset() {
  const layout = readHeaderLayout();
  return layout ? layout.offset : null;
}

function syncMapTopCssVar(mapTopPx) {
  if (typeof document === 'undefined' || mapTopPx == null) {
    return;
  }
  document.documentElement.style.setProperty(
    SITE_MAP_TOP_VAR,
    `${Math.ceil(mapTopPx)}px`,
  );
}

const OFFSET_CHANGE_THRESHOLD_PX = 1;

export function useHeaderOffset(pathname, isLoggedIn) {
  const { resultsSearchExpanded } = useResultsSearchContext();
  const isCompactUx = usesCompactSearchBar(pathname);
  const compactCollapsed = isCompactUx && !resultsSearchExpanded;

  const fallbackOffset = getHeaderOffset(pathname, isLoggedIn);
  const [layout, setLayout] = useState(() => ({
    height: fallbackOffset,
    offset: fallbackOffset,
    mapTop: fallbackOffset + 12,
  }));

  useEffect(() => {
    const fallback = getHeaderOffset(pathname, isLoggedIn);
    const next = { height: fallback, offset: fallback, mapTop: fallback + 12 };
    setLayout(next);
    syncSiteHeaderOffsetCssVar(fallback);
    syncMapTopCssVar(next.mapTop);
  }, [pathname, isLoggedIn]);

  useEffect(() => {
    let rafId = null;

    const measure = () => {
      if (rafId != null) {
        cancelAnimationFrame(rafId);
      }
      rafId = requestAnimationFrame(() => {
        rafId = null;
        const measured = readHeaderLayout();
        if (measured != null) {
          syncSiteHeaderOffsetCssVar(measured.height);
          syncMapTopCssVar(measured.mapTop);
          setLayout(prev => {
            if (
              Math.abs(prev.offset - measured.offset) <=
                OFFSET_CHANGE_THRESHOLD_PX &&
              Math.abs(prev.mapTop - measured.mapTop) <=
                OFFSET_CHANGE_THRESHOLD_PX
            ) {
              return prev;
            }
            return measured;
          });
        }
      });
    };

    measure();

    const header = document.getElementById(SITE_HEADER_ID);
    if (!header) {
      return undefined;
    }

    const resizeObserver = new ResizeObserver(measure);
    resizeObserver.observe(header);
    window.addEventListener('resize', measure);

    return () => {
      if (rafId != null) {
        cancelAnimationFrame(rafId);
      }
      resizeObserver.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [pathname, isLoggedIn, resultsSearchExpanded, compactCollapsed]);

  return layout;
}
